import Link from "next/link";
import { useTranslation } from "next-i18next";
import { useEffect } from "react";
import Logo from "./Logo";
import SearchInput from "./SearchInput";
import NavMenu from "./NavMenu";
import { Button } from "antd";
import Cart from "./Cart";
import SwitchIb from "./SwitchIb";
import HeaderUser from "./User";
import { getCookie } from "cookies-next";
import { cartCountApi } from "@/api/cart";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setCartTotal } from "@/store/cart";
import { useRouter } from "next/router";
import { setToken as setUserToken, setUserInfo } from "@/store/user";
import { RootState } from "@/store/index";

// 防御：userInfo cookie 可能是脏值（如跨站/免登异常时被写入的 "undefined"），
// 直接 JSON.parse 会抛错导致整页崩溃（渲染 /_error），这里安全解析、失败返回 undefined。
const parseUserInfo = (raw: any) => {
  if (!raw || raw === "undefined" || raw === "null") return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

const Header = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const site = useAppSelector((state) => state.app.site);
  const token = useAppSelector((state: RootState) => state.user.token);
  const userInfo = useAppSelector((state: RootState) => state.user.userInfo);

  // 获取购物车商品数量
  const getCartNum = async () => {
    const data: any = await cartCountApi();
    const count = data?.count ?? 0;
    dispatch(setCartTotal(count));
  };

  // 初始化用户状态（token + userInfo）
  const initUserState = () => {
    const token = getCookie("token")?.toString();
    const rawUserInfo = getCookie("userInfo");

    dispatch(setUserToken(token || undefined));

    if (token) {
      getCartNum();
    } else {
      dispatch(setCartTotal(0));
    }

    dispatch(setUserInfo(parseUserInfo(rawUserInfo)));
  };

  useEffect(() => {
    if (router.pathname !== "/login") {
      initUserState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname]);

  // 页面切回时同步 cookie 状态
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const token = getCookie("token");
        if (!token) {
          initUserState(); // 手动同步 UI
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header className={`sticky top-0 z-40 hidden h-86px w-full justify-center bg-white lg:flex`}>
      <div className="flex h-full w-full items-center justify-between lg:w-md-container xl:w-container">
        <div className="flex h-full items-center">
          <Logo />
          <NavMenu title={t("menu.about")} type={0} url="/about" />
          <NavMenu title={t("menu.product_category")} type={1} url="/mall" />
          {site && site === "cn" && (
            <>
              <NavMenu title={t("menu.discounts_activities")} type={0} url="/discount" />
              <NavMenu title={t("menu.csr")} type={1} url="/csr" />
            </>
          )}
        </div>
        <div className="flex items-center justify-end">
          <SearchInput />
          <div className="ml-6 h-8 w-0.5 bg-gray-600"></div>

          {/* 未登录 */}
          {!token && (
            <Link href="/login" className="ml-8">
              <Button
                type="primary"
                size="large"
                style={{
                  padding: "12px 16px",
                  height: "48px",
                  minWidth: "150px",
                }}
              >
                {t("menu.login_and_reg")}
              </Button>
            </Link>
          )}

          {/* 已登录 */}
          {token && userInfo && (
            <>
              {userInfo.canSwitchUser && (
                <SwitchIb
                  utype={userInfo.utype}
                  refreshUserInfo={() => {
                    const raw = getCookie("userInfo");
                    dispatch(setUserInfo(parseUserInfo(raw)));
                  }}
                />
              )}
              <HeaderUser uuid={userInfo.uuid} />
            </>
          )}

          <Cart />
        </div>
      </div>
    </header>
  );
};

export default Header;
